import camelCase from 'lodash/camelCase';
import { firestoreAction } from 'vuexfire';

import { db } from '../../firebase';

/**
 * Use when you need to select a particular doc in a collection for editing.
 * @param {Object} api Api for selected resource.
 * @param {Boolean} [hasReferences=false] Specify if selected document has references.
 * If `true` then select action will try to load those references also.
 * @param {Boolean} [bindSelected=false] Specify if selected document should be bound
 * to the selected. ignored if `hasReferences` is set to true.
 *
 * @returns {Function} that will take a module and apply the plugin.
 */
export default function(api, hasReferences = false, bindSelected = false) {
  return function(module) {
    const ucName = module.name.toUpperCase();
    const GET_INIT = `${ucName}_GET_SELECTED_INIT`;
    const GET_ERROR = `${ucName}_GET_SELECTED_ERROR`;
    const GET_DONE = `${ucName}_GET_SELECTED_DONE`;
    module.addMutationTypes([GET_INIT, GET_ERROR, GET_DONE]);

    const state = {
      selected: {},
      loadingSelected: false,
      selectError: null
    };

    const mutations = {
      [GET_INIT](state) {
        state.loadingSelected = true;
        state.selectError = null;
      },
      [GET_DONE](state, data) {
        if (!bindSelected) state.selected = data;
        state.loadingSelected = false;
      },
      [GET_ERROR](state, error) {
        state.selectError = error;
        state.loadingSelected = false;
      }
    };

    const actions = {
      [camelCase(`select_${module.name}`)]: async function(
        { commit, dispatch },
        id
      ) {
        commit(module.con.LOADING_INIT, null, { root: true });
        commit(GET_INIT);
        try {
          if (bindSelected && !hasReferences) {
            await dispatch(
              'bindSelected',
              db.collection(module.moduleName).doc(id)
            );
            commit(GET_DONE);
          } else {
            const data = await api.get(id);
            if (hasReferences) await dispatch('bindReferences', data);
            commit(GET_DONE, data);
          }
        } catch (error) {
          commit(GET_ERROR, error);
        }
        commit(module.con.LOADING_DONE, null, { root: true });
      },
      bindSelected: firestoreAction(async ({ bindFirestoreRef }, ref) => {
        await bindFirestoreRef('selected', ref, { maxRefDepth: 1 });
      })
    };

    return module.extend({ state, mutations, actions });
  };
}
